import { FC } from 'react';
import { useTranslation } from 'next-i18next';

import { Button } from '@phoenix/design-system/frontend/ui/button/button';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { mapProductPowerEventData } from '@phoenix/team-m/dashboard/frontend/util/product/product.mapper';
import { ProductCategory, ProductDriveType } from '@phoenix/team-m/dashboard/frontend/util/product/product.model';
import { useImpressionEvent } from '@phoenix/core/frontend/shared/util/hooks/use-impression-event';
import { ecommerceEvents } from '@phoenix/team-m/dashboard/frontend/shared/tracking/ecommerce.events';

import { BatteryCompatibilityList } from './battery-compatibility-list';
import { ProductDetails } from './product-details';
import { ProductImage } from './product-image';

const externalLinkIcon = getDesignSystemIcon('icon-external-link-16.svg');

export interface ProductRecommendedCardProps {
  imageUrl: string;
  productName: string;
  articleNumber: string;
  category?: ProductCategory;
  driveType?: ProductDriveType;
  maxRuntime?: number | null;
  compatibleBatteries?: { productName: string }[];
  callToActionUrl?: string | null;
  index: number;
}

export const ProductRecommendedCard: FC<ProductRecommendedCardProps> = (props) => {
  const { imageUrl, productName, articleNumber, category, driveType, maxRuntime, compatibleBatteries, callToActionUrl, index } = props;
  const { t } = useTranslation();

  const productEventData = {
    item_id: articleNumber,
    item_name: productName,
    product_power: mapProductPowerEventData(productName),
    item_list_name: 'recommended_products_nbo',
    picture_url: imageUrl,
    index,
    ...(category?.name && { item_category: category.name }),
  };

  return (
    <div
      className="flex h-full flex-col justify-between overflow-hidden rounded-lg border border-secondary-mid bg-primary-white p-6"
      data-testid="product-recommended-card"
      ref={useImpressionEvent(() => ecommerceEvents.impression(productEventData))}
    >
      <div>
        <ProductImage src={imageUrl} alt={productName} />
        <ProductDetails name={productName} articleNumber={articleNumber} runtime={maxRuntime} category={category} driveType={driveType} />
        {compatibleBatteries && <BatteryCompatibilityList batteries={compatibleBatteries.map((b) => b.productName)} />}
      </div>
      {callToActionUrl && (
        <Button
          as="a"
          target="_blank"
          href={callToActionUrl}
          size="small"
          className="mt-6 w-full select-none"
          btnType="white_default"
          iconPosition="left"
          icon={externalLinkIcon}
          data-testid="view-product-btn"
          onClick={() => ecommerceEvents.selection(productEventData)}
        >
          {t('dashboard_compatibleProducts_card_button_view')}
        </Button>
      )}
    </div>
  );
};
